import React from "react";
import ApiStory from "models/data/api/ApiStory";
import AuthorMetaTagsQuery from "./AuthorMetaTagsQuery";
import { NewsArticleSchema } from "./NewsArticleSchema";
import { DefaultSeo, NextSeo } from "next-seo";
import {
  getStoryPublicationDate,
  getStoryTitle,
  getStoryFeaturedImageUrl,
  getStoryAuthor,
  getStorySummary,
} from "data/api/ApiStory";
import { getAuthorFullName } from "data/api/ApiAuthor";
import ApiAuthor from "models/data/api/ApiAuthor";
import ApiCategory from "models/data/api/ApiCategory";
import CategoryMetaTagsQuery from "./CategoryMetaTagsQuery";
import { NewsMediaOrganizationSchema } from "components/shared/MetaTags/NewsMediaOrganizationSchema";
import { baseUrl } from "services/shared/Environment";

export const DefaultMetaTags = (): JSX.Element => {
  const description =
    "We're The Juggernaut. We tell in-depth, nuanced stories on South Asians around the world. Join thousands and subscribe today.";
  return (
    <>
      <NewsMediaOrganizationSchema />
      <DefaultSeo
        title={"The South Asian stories you need to know | The Juggernaut"}
        description={description}
        twitter={{ cardType: "summary_large_image" }}
        openGraph={{
          title: "The Juggernaut",
          url: "https://www.thejuggernaut.com",
          type: "website",
          description: description,
          images: [
            {
              url: "https://www.thejuggernaut.com/static/preview_image.png",
            },
          ],
          site_name: "The Juggernaut",
        }}
      />
    </>
  );
};

export const StoryMetaTags = ({
  story,
  storyUrl,
}: {
  story: ApiStory;
  storyUrl: string;
}): JSX.Element => {
  return (
    <>
      <NewsArticleSchema
        storyUrl={storyUrl}
        title={getStoryTitle(story)}
        imageUrl={getStoryFeaturedImageUrl(story)}
        publicationDate={getStoryPublicationDate(story)}
        authorName={getAuthorFullName(getStoryAuthor(story))}
        storySummary={getStorySummary(story)}
      />
      <NextSeo
        title={`${ApiStory.title(story)} | The Juggernaut`}
        description={ApiStory.summary(story)}
        twitter={{ cardType: "summary_large_image" }}
        canonical={storyUrl.split("?")[0]}
        openGraph={{
          title: ApiStory.title(story),
          url: storyUrl,
          type: "website",
          description: ApiStory.summary(story),
          images: [{ url: `https:${ApiStory.featuredImageUrl(story)}` }],
          site_name: "The Juggernaut",
        }}
      />
    </>
  );
};

interface AuthorMetaTagsProps {
  authorSlug: string;
  authorUrl: string;
}

export const AuthorMetaTags = ({
  authorSlug,
  authorUrl,
}: AuthorMetaTagsProps): JSX.Element => (
  <AuthorMetaTagsQuery slug={authorSlug}>
    {({ author }) => (
      <>
        <NextSeo
          title={`${ApiAuthor.fullName(
            author
          )}'s latest stories | The Juggernaut`}
          description={ApiAuthor.metaDescription(author)}
          twitter={{ cardType: "summary_large_image" }}
          openGraph={{
            title: `${ApiAuthor.fullName(author)}`,
            url: authorUrl,
            type: "website",
            description: ApiAuthor.metaDescription(author),
            images: [{ url: `${ApiAuthor.pictureUrl(author)}` }],
            site_name: "The Juggernaut",
          }}
          canonical={authorUrl}
        />
      </>
    )}
  </AuthorMetaTagsQuery>
);

interface CategoryMetaTagsProps {
  categorySlug: string;
  categoryUrl: string;
}

export const CategoryMetaTags = ({
  categorySlug,
  categoryUrl,
}: CategoryMetaTagsProps): JSX.Element => (
  <CategoryMetaTagsQuery slug={categorySlug}>
    {({ category }) => (
      <>
        <NextSeo
          title={
            categorySlug === "crossword"
              ? "Crossword puzzles with South Asian themes | The Juggernaut"
              : `${ApiCategory.categoryName(
                  category
                )} - South Asian News & Stories | The Juggernaut`
          }
          description={ApiCategory.metaDescription(category)}
          twitter={{ cardType: "summary_large_image" }}
          openGraph={{
            title: `${ApiCategory.categoryName(category)}`,
            url: categoryUrl,
            type: "website",
            site_name: "The Juggernaut",
            description: ApiCategory.metaDescription(category),
          }}
        />
      </>
    )}
  </CategoryMetaTagsQuery>
);

export const SubscribeMetaTags = () => (
  <NextSeo
    title="Subscribe to The Juggernaut"
    canonical={`${baseUrl()}/subscribe`}
  />
);
